/**
 * Keyboard and gamepad (docs/spec/04 §7). The game reads `keys`; menus get `MenuAction`s with key
 * repeat; raw keys go to the scene first so text entry and rebinding work.
 */

import type { Action, OptionsStore } from '../platform/options';
import { ACTIONS } from '../platform/options';
import type { KeyInput, MenuAction } from '../ui/scene';
import type { GameKey } from './gameKeys';
import { GameKeys } from './gameKeys';

export interface ControlsHandlers {
  /** Raw key first; true = consumed, no action follows. */
  key(k: KeyInput): boolean;
  action(a: MenuAction): void;
  /** Any key, button or touch at all (audio unlock, idle timers). */
  any(): void;
}

const MENU_DIRS: readonly Action[] = ['left', 'right', 'up', 'down'];
const GAME_KEYS: readonly Action[] = ['left', 'right', 'fire', 'bomb'];
const ALWAYS_MENU = ['Enter', 'NumpadEnter', 'Escape', 'Backspace', 'Tab'];
const REPEAT_DELAY = 360;
const REPEAT_RATE = 95;
const PAD_DEAD = 0.5;

export class Controls {
  readonly keys = new GameKeys();
  private handlers: ControlsHandlers | null = null;
  private repeat: { source: string; action: MenuAction; wait: number } | null = null;
  private padHeld = new Set<string>();
  private padSeen = false;
  /** A game is being played: the pad's B drops a Nova and is not also "back". */
  inGame = false;

  constructor(private readonly options: OptionsStore) {}

  attach(handlers: ControlsHandlers): void {
    this.handlers = handlers;
    window.addEventListener('keydown', (e) => this.down(e));
    window.addEventListener('keyup', (e) => this.up(e.code));
    window.addEventListener('blur', () => this.reset());
    window.addEventListener('gamepadconnected', () => {
      this.padSeen = true;
    });
  }

  /** Let go of everything (focus lost, a scene change). */
  reset(): void {
    this.keys.reset();
    this.repeat = null;
  }

  get gamepad(): boolean {
    return this.padSeen;
  }

  /** The action a key is bound to, if any. */
  actionOf(code: string): Action | null {
    const keys = this.options.get().keys;
    for (const a of ACTIONS) if (keys[a].includes(code)) return a;
    return null;
  }

  private down(e: KeyboardEvent): void {
    const h = this.handlers;
    if (!h) return;
    h.any();
    const action = this.actionOf(e.code);
    const ours = action !== null || ALWAYS_MENU.includes(e.code);
    if (e.repeat) {
      if (ours) e.preventDefault();
      return;
    }
    if (h.key({ code: e.code, key: e.key, shift: e.shiftKey, ctrl: e.ctrlKey || e.metaKey })) {
      e.preventDefault();
      return;
    }
    if (!ours) return;
    e.preventDefault();
    this.press(`k:${e.code}`, action, e.code);
  }

  private up(code: string): void {
    this.lift(`k:${code}`);
  }

  /** A key or button went down: the game's held state, and the menu action it means. */
  private press(source: string, action: Action | null, code = ''): void {
    const h = this.handlers;
    if (!h) return;
    if (action !== null && GAME_KEYS.includes(action)) this.keys.set(source, action as GameKey, true);
    if (action !== null && MENU_DIRS.includes(action)) {
      h.action(action as MenuAction);
      this.repeat = { source, action: action as MenuAction, wait: REPEAT_DELAY };
    } else if (code === 'Escape' || code === 'Backspace') h.action('back');
    else if (action === 'fire' || code === 'Enter' || code === 'NumpadEnter') h.action('confirm');
    else if (action === 'pause') h.action('pause');
  }

  private lift(source: string): void {
    this.keys.release(source);
    if (this.repeat?.source === source) this.repeat = null;
  }

  /** Call every frame: key repeat and the gamepad. */
  update(dtMs: number): void {
    this.pollPad();
    const r = this.repeat;
    if (!r) return;
    r.wait -= dtMs;
    while (r.wait <= 0 && this.repeat === r) {
      r.wait += REPEAT_RATE;
      this.handlers?.action(r.action);
    }
  }

  private pollPad(): void {
    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    const pad = [...pads].find((p) => p !== null && p.connected);
    const now = new Set<string>();
    if (pad) {
      this.padSeen = true;
      const b = (i: number): boolean => pad.buttons[i]?.pressed === true;
      const ax = pad.axes[0] ?? 0;
      const ay = pad.axes[1] ?? 0;
      if (b(12) || ay < -PAD_DEAD) now.add('up');
      if (b(13) || ay > PAD_DEAD) now.add('down');
      if (b(14) || ax < -PAD_DEAD) now.add('left');
      if (b(15) || ax > PAD_DEAD) now.add('right');
      if (b(0)) now.add('fire');
      if (b(1) || b(2)) now.add('bomb');
      if (b(9)) now.add('pause');
    }
    for (const name of now) {
      if (this.padHeld.has(name)) continue;
      this.handlers?.any();
      this.press(`p:${name}`, name as Action);
      if (name === 'bomb' && !this.inGame) this.handlers?.action('back');
    }
    for (const name of this.padHeld) if (!now.has(name)) this.lift(`p:${name}`);
    this.padHeld = now;
  }
}
